import { useState, useEffect } from 'react';
import { Plus, Trash2, Search, Save, ArrowLeft } from 'lucide-react';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { api } from '../../services/api';
import { useConfirm } from '../../hooks/use-confirm';
import { toast } from 'sonner';

interface ColumnDef {
    key: string;
    label: string;
    type?: 'text' | 'number';
}

interface MasterDataCrudProps {
    title: string;
    tableName: string;
    columns: ColumnDef[];
    pkField: string;
}

export function MasterDataCrud({ title, tableName, columns, pkField }: MasterDataCrudProps) {
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [newItem, setNewItem] = useState<any>({});
    const [editingId, setEditingId] = useState<number | null>(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await api.masterData.list(tableName);
            setData(res);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [tableName]);

    const { confirm } = useConfirm();

    const handleSave = async () => {
        try {
            if (editingId) {
                const { [pkField]: _ignore, ...payload } = newItem;
                await api.masterData.update(tableName, editingId, payload);
            } else {
                await api.masterData.create(tableName, newItem);
            }
            setIsAddOpen(false);
            setNewItem({});
            setEditingId(null);
            fetchData();
        } catch (e) {
            toast.error("Failed to save");
        }
    };

    const handleDelete = async (id: number) => {
        const ok = await confirm('Are you sure?', { variant: 'destructive', confirmText: 'Delete', cancelText: 'Cancel' });
        if (!ok) return;
        try {
            await api.masterData.delete(tableName, id);
            toast.success('Deleted');
            fetchData();
        } catch (e) {
            toast.error("Failed to delete");
        }
    };

    const filteredData = data.filter(item =>
        columns.some(col => String(item[col.key] || '').toLowerCase().includes(search.toLowerCase()))
    );

    return (
        <div className="space-y-6">
            {/* Header / Breadcrumb */}
            <div className="flex items-center gap-4">
                {(isAddOpen || editingId) && (
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                            setIsAddOpen(false);
                            setNewItem({});
                            setEditingId(null);
                        }}
                    >
                        <ArrowLeft className="w-5 h-5 text-slate-500" />
                    </Button>
                )}
                <h2 className="text-xl font-semibold text-slate-800">
                    {isAddOpen ? `New ${title}` : editingId ? `Edit ${title}` : title}
                </h2>
            </div>

            {isAddOpen || editingId ? (
                // --- FORM VIEW ---
                <Card className="max-w-2xl">
                    <CardHeader>
                        <CardTitle>{isAddOpen ? 'Create New Record' : 'Edit Record'}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="grid gap-6">
                            {columns.map(col => (
                                <div key={col.key} className="space-y-2">
                                    <label className="text-sm font-medium text-slate-700">{col.label}</label>
                                    <Input
                                        type={col.type || 'text'}
                                        value={newItem[col.key] || ''}
                                        onChange={e => setNewItem({ ...newItem, [col.key]: e.target.value })}
                                        className="max-w-md"
                                        placeholder={`Enter ${col.label.toLowerCase()}`}
                                    />
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-3 pt-4">
                            <Button onClick={handleSave} className="w-32">
                                <Save className="w-4 h-4 mr-2" /> Save
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setIsAddOpen(false);
                                    setNewItem({});
                                    setEditingId(null);
                                }}
                            >
                                Cancel
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            ) : (
                // --- LIST VIEW ---
                <Card className="h-[calc(100vh-200px)] flex flex-col">
                    <CardHeader className="flex flex-row items-center justify-between py-4 border-b bg-slate-50/50">
                        <div className="relative w-64">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                            <Input
                                placeholder="Search records..."
                                className="pl-8 h-9 bg-white"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>
                        <Button onClick={() => { setIsAddOpen(true); setEditingId(null); setNewItem({}); }}>
                            <Plus className="w-4 h-4 mr-2" /> Add {title}
                        </Button>
                    </CardHeader>
                    <CardContent className="flex-1 overflow-auto p-0">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 sticky top-0 z-10">
                                <tr>
                                    {columns.map(col => (
                                        <th key={col.key} className="px-6 py-3 text-left font-medium text-slate-500 border-b">
                                            {col.label}
                                        </th>
                                    ))}
                                    <th className="px-6 py-3 w-24 border-b text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {loading ? (
                                    <tr><td colSpan={columns.length + 1} className="p-8 text-center text-slate-500">Loading...</td></tr>
                                ) : filteredData.length === 0 ? (
                                    <tr><td colSpan={columns.length + 1} className="p-12 text-center text-slate-500 bg-slate-50/30">No records found</td></tr>
                                ) : (
                                    filteredData.map((row) => (
                                        <tr key={row[pkField]} className="hover:bg-slate-50 group transition-colors">
                                            {columns.map(col => (
                                                <td key={col.key} className="px-6 py-3 align-middle text-slate-700">
                                                    {row[col.key]}
                                                </td>
                                            ))}
                                            <td className="px-6 py-3 text-right">
                                                <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-all">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-8 px-2 text-xs"
                                                        onClick={() => {
                                                            setEditingId(row[pkField]);
                                                            setNewItem(row);
                                                            setIsAddOpen(false);
                                                        }}
                                                    >
                                                        Edit
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 text-slate-400 hover:text-red-600"
                                                        onClick={() => handleDelete(row[pkField])}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
